import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import UnifiedTradingTab from './trading/UnifiedTradingTab';

const VALID_TABS = ['open', 'pending', 'closed'] as const;
const DEFAULT_TAB = 'open';

interface TradesPageProps {}

const TradesPage: React.FC<TradesPageProps> = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab = VALID_TABS.includes(tabParam as (typeof VALID_TABS)[number])
        ? (tabParam as (typeof VALID_TABS)[number])
        : DEFAULT_TAB;

    // Ensure tab is in URL so refresh keeps context
    useEffect(() => {
        if (!searchParams.has('tab')) {
            setSearchParams(
                prev => {
                    const p = new URLSearchParams(prev);
                    p.set('tab', DEFAULT_TAB);
                    return p;
                },
                { replace: true }
            );
        }
    }, [searchParams, setSearchParams]);

    const handleTabChange = (value: string) => {
        if (!VALID_TABS.includes(value as (typeof VALID_TABS)[number])) return;
        // Replace URL with only tab so filters/sort/pagination reset when switching tabs
        setSearchParams(new URLSearchParams({ tab: value }));
    };

    return (
        <PageContainer>
            <PageHeader
                title="Trades"
                description="View and manage all trading activities across the platform"
            />

            <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
                <TabsList>
                    <TabsTrigger value="open">Open Positions</TabsTrigger>
                    <TabsTrigger value="pending">Pending Orders</TabsTrigger>
                    <TabsTrigger value="closed">Trade History</TabsTrigger>
                </TabsList>

                <TabsContent value="open">
                    <UnifiedTradingTab
                        status="open"
                        title="Open Positions"
                        description="Active trades currently running"
                    />
                </TabsContent>

                <TabsContent value="pending">
                    <UnifiedTradingTab
                        status="pending"
                        title="Pending Orders"
                        description="Orders waiting for execution or price conditions to be met"
                    />
                </TabsContent>

                <TabsContent value="closed">
                    <UnifiedTradingTab
                        status="closed"
                        title="Trade History"
                        description="Completed trades and their performance"
                    />
                </TabsContent>
            </Tabs>
        </PageContainer>
    );
};

export default TradesPage;
